import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAllDocentes } from "../../services/docente.service";
import { getAllIdiomasByIdDocente } from "../../services/idioma.service";
import { getAllGruposByIdDocenteIdIdioma } from "../../services/grupo.service";
import { Link } from "react-router-dom";
import { ContenedorPageTemplate } from "../../components/templates/ContenedorPageTemplate";
import { BookOpen, Calendar, ClipboardCheck, Download, FileText, History, Layers, Users } from "lucide-react";

export const InicioDocente = () => {
  const [docenteActual, setDocenteActual] = useState(null);
  const [idiomasDocente, setIdiomasDocente] = useState(null);
  const [totalGruposActivos, setTotalGruposActivos] = useState(0);
  const [error, setError] = useState(null);

  const userName = useSelector((store) => store.usuario.username);

  useEffect(() => {
    const obtenerDocentesYBuscarIdiomas = async () => {
      try {
        const docentesData = await getAllDocentes();

        const docente = docentesData.find(
          (est) => est.usuario.username === userName
        );

        if (docente) {
          setDocenteActual(docente);
          const idiomas = await getAllIdiomasByIdDocente(
            docente.docente.idDocente
          );
          setIdiomasDocente(idiomas || []);

          // Calcular grupos activos
          if (idiomas && idiomas.length > 0) {
            let gruposActivos = 0;
            for (const idioma of idiomas) {
              try {
                const grupos = await getAllGruposByIdDocenteIdIdioma(
                  docente.docente.idDocente,
                  idioma.idIdioma
                );
                
                // Filtrar grupos que no deben ocultarse (no han pasado 15 días desde su fecha fin)
                const gruposValidos = grupos.filter(grupo => {
                  if (!grupo?.fechaFin) return true;
                  
                  const hoy = new Date();
                  const fechaFin = new Date(grupo.fechaFin);
                  
                  hoy.setHours(0, 0, 0, 0);
                  fechaFin.setHours(0, 0, 0, 0);
                  
                  const fechaFinMas15Dias = new Date(fechaFin);
                  fechaFinMas15Dias.setDate(fechaFinMas15Dias.getDate() + 15);
                  
                  return hoy <= fechaFinMas15Dias;
                });
                
                gruposActivos += gruposValidos.length;
              } catch (err) {
                console.error(`Error obteniendo grupos para idioma ${idioma.idIdioma}:`, err);
              }
            }
            setTotalGruposActivos(gruposActivos);
          }
        } else {
          setError("Docente no encontrado");
          setIdiomasDocente([]);
        }
      } catch (err) {
        setError("Error al obtener datos");
        console.error(err);
        setIdiomasDocente([]);
      }
    };
    obtenerDocentesYBuscarIdiomas();
  }, [userName]);

  const nombreCompleto = useMemo(() => {
    const nombres = [
      docenteActual?.persona?.nombre,
      docenteActual?.persona?.apellido,
    ]
      .filter(Boolean)
      .join(" ");
    return nombres || docenteActual?.usuario?.username || "Docente";
  }, [docenteActual]);

  return (
    <ContenedorPageTemplate fondo={false} objetos={idiomasDocente}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        {/* Header Hero - Simplificado */}
        <section className="relative overflow-hidden bg-gradient-to-r from-theme-blue via-blue-600 to-blue-700 text-white">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-theme-yellow rounded-full blur-3xl" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-white/80">Panel Docente</p>
                <h1 className="text-2xl md:text-3xl font-bold">
                  Bienvenido, {nombreCompleto}
                </h1>
              </div>
            </div>
            <p className="text-white/90 max-w-2xl text-sm md:text-base">
              Administra tus grupos, registra asistencia y mantén el seguimiento académico de tus estudiantes.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          {error && (
            <div className="mb-6 rounded-xl border-l-4 border-red-500 bg-red-50 px-6 py-4">
              <p className="font-semibold text-red-800">Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Stats Cards - Separadas del hero */}
          <section className="mb-10">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Resumen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-theme-blue/10 flex items-center justify-center">
                    <Layers className="h-6 w-6 text-theme-blue" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{idiomasDocente?.length || 0}</p>
                    <p className="text-sm text-gray-600">Idiomas asignados</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{totalGruposActivos}</p>
                    <p className="text-sm text-gray-600">Grupos activos</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">—</p>
                    <p className="text-sm text-gray-600">Clases programadas</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Idiomas Section */}
          <section>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Mis Idiomas
              </h2>
              <p className="text-gray-600">
                Selecciona un idioma para acceder a tus grupos activos y al historial de clases.
              </p>
            </div>

            {idiomasDocente && idiomasDocente.length > 0 ? (
              <IdiomasList idiomas={idiomasDocente} />
            ) : (
              <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-16 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-200">
                  <Layers className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-1">
                  Sin idiomas asignados
                </h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  Actualmente no tienes idiomas asignados. Contacta con administración si consideras que esto es un error.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </ContenedorPageTemplate>
  );
};

const IdiomasList = ({ idiomas }) => (
  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {idiomas.map((idioma) => (
      <li
        key={idioma?.idIdioma}
        className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
      >
        {/* Header con gradiente */}
        <div className="relative bg-gradient-to-br from-theme-blue to-blue-700 px-6 py-6 text-white">
          <div className="absolute top-0 right-0 -mr-8 -mt-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-2xl font-bold capitalize mb-1">
                {idioma?.nombre}
              </h4>
              <span className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 px-3 py-1 text-xs font-bold tracking-wide">
                {idioma?.sigla}
              </span>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm border border-white/30">
              <BookOpen className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Accede a tus grupos activos, gestiona la asistencia y consulta el historial académico de este idioma.
          </p>

          {/* Action Buttons */}
          <div className="space-y-2 pt-2">
            <Link
              to={`/idiomas/grupos/${idioma?.idIdioma}`}
              className="group/btn flex items-center justify-center gap-2 rounded-xl bg-theme-blue px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-blue-700 hover:shadow-lg"
            >
              <Users className="h-4 w-4" />
              <span>Ver grupos activos</span>
            </Link>
            <Link
              to={`/idiomas/grupos/${idioma?.idIdioma}/historial`}
              className="group/btn flex items-center justify-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-theme-blue hover:bg-blue-50 hover:text-theme-blue"
            >
              <History className="h-4 w-4" />
              <span>Historial de grupos</span>
            </Link>
          </div>
        </div>

        {/* Hover Effect Border */}
        <div className="absolute inset-0 rounded-2xl border-2 border-transparent transition-colors group-hover:border-theme-blue/50 pointer-events-none" />
      </li>
    ))}
  </ul>
);
